import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { navigate, useLocation } from "../lib/router";
import { Icon, type IconName } from "./Icon";
import { pillCls } from "./ui";

export type TabItem<K extends string> = {
  key: K;
  label: ReactNode;
  /** A picture on the left of the label, where the page already has one for the thing. */
  icon?: IconName;
  /** How many items are under the tab, a quiet number on the right of the label. */
  count?: number | null;
};

/**
 * The one tab bar: grey pill, the open tab a white pill; `size="sm"` is the subtle one. Always one line; when it is wider than the screen
 * it scrolls sideways and fades at the edge that has more. Arrow keys move between tabs.
 */
export function Tabs<K extends string>({
  tabs,
  value,
  onChange,
  label,
  size = "md",
  className = "",
}: {
  tabs: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  /** What the tabs choose between, for screen readers ("Level", "Section"). */
  label: string;
  /** `sm` is the subtle one, for a secondary choice inside a section (a feed's source, a transcript's kind). */
  size?: "md" | "sm";
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ left: false, right: false });
  const measure = () => {
    const el = ref.current;
    if (el) setFade({ left: el.scrollLeft > 1, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1 });
  };
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Bring the open tab into view when the bar starts scrolled past it (a phone, a deep link).
    const open = el.querySelector<HTMLElement>('[aria-selected="true"]');
    if (open && (open.offsetLeft < el.scrollLeft || open.offsetLeft + open.offsetWidth > el.scrollLeft + el.clientWidth)) {
      el.scrollLeft = open.offsetLeft - (el.clientWidth - open.offsetWidth) / 2;
    }
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [tabs.length]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const at = tabs.findIndex((t) => t.key === value);
    const next = tabs[(at + (e.key === "ArrowRight" ? 1 : tabs.length - 1)) % tabs.length]!;
    onChange(next.key);
    ref.current?.querySelector<HTMLElement>(`[data-tab="${next.key}"]`)?.focus();
  };

  const edge = "24px";
  const mask =
    fade.left || fade.right
      ? `linear-gradient(to right, ${fade.left ? `transparent, black ${edge}` : "black"}, ${fade.right ? `black calc(100% - ${edge}), transparent` : "black"})`
      : undefined;

  return (
    <div
      ref={ref}
      role="tablist"
      aria-label={label}
      onScroll={measure}
      onKeyDown={onKeyDown}
      style={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
      className={`flex w-fit max-w-full gap-0.5 overflow-x-auto rounded-full bg-fill [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${size === "sm" ? "p-0.5 text-xs font-medium" : "p-1 text-sm"} ${className}`}
    >
      {tabs.map((t) => {
        const open = t.key === value;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            data-tab={t.key}
            aria-selected={open}
            tabIndex={open ? 0 : -1}
            onClick={() => onChange(t.key)}
            className={`${size === "sm" ? `rounded-full px-2.5 py-0.5 transition ${open ? "bg-raised text-fg shadow-sm" : "text-muted hover:text-fg"}` : pillCls(open)} inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap`}
          >
            {t.icon && <Icon name={t.icon} className="size-3.5" />}
            {t.label}
            {t.count !== undefined && t.count !== null && <span className="text-faint tabular-nums">{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A page's open tab, kept in the address (`?tab=…`), so reload, Back and a shared link land on it.
 * The first key is the default and leaves the address clean. Tabs inside a popup keep their own state instead.
 */
export function useTabParam<K extends string>(keys: readonly K[], param = "tab"): [K, (key: K) => void] {
  const { pathname, search } = useLocation();
  const raw = search.get(param);
  const value = keys.find((k) => k === raw) ?? keys[0]!;
  const set = (key: K) => {
    const next = new URLSearchParams(location.search);
    if (key === keys[0]) next.delete(param);
    else next.set(param, key);
    const query = next.toString();
    navigate(`${pathname}${query ? `?${query}` : ""}`, true);
  };
  return [value, set];
}
